// photo_preview -- renders thumbnails of a photo question's current value
// beneath the in-app Take Photo button.
//
// The field result editor hides SurveyJS's own capture control and injects a
// Take Photo button. SurveyJS's value preview can be absent or empty for these
// questions, so this renders a guaranteed reflection of question.value so the
// tasker always sees the photo they captured. Pure DOM + a value array in,
// thumbnails out -- shared by the controller and its PWA spec.

// Map a SurveyJS file-question value to a flat list of displayable URLs.
// SurveyJS stores [{ name, type, content }]; content is a server or blob: URL.
// Plain string entries (legacy) are passed through.
export function previewUrls(value) {
  if (!Array.isArray(value)) return []
  return value
    .map((v) => (typeof v === "string" ? v : v && (v.content || v.url)))
    .filter(Boolean)
}

// The delete button's SVG -- a plain X, red-600, matching icon-patterns.md's
// delete convention. The button itself is sized h-11 w-11 (44px, the
// standard touch target) even though the visible badge is smaller, same
// overlap-the-corner shape as a native photo picker's remove control.
const DELETE_BUTTON_HTML = `
  <button type="button"
          class="absolute -top-2 -right-2 h-11 w-11 flex items-center justify-center"
          data-photo-delete-url="__URL__"
          aria-label="Delete photo"
          title="Delete photo">
    <span class="flex items-center justify-center h-6 w-6 rounded-full bg-white border border-slate-300 shadow-sm text-red-600">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </span>
  </button>
`

// Render (or clear) the preview strip inside `el`, reflecting `value`. Creates
// the strip on first call, reuses it after. Hidden when there's nothing to show.
//
// Thumbs are tappable (card #4368): the shared pwa-image-viewer controller
// lives outside this strip's DOM scope (it wraps only the camera sheet, see
// field/responses/show.html.erb), so a plain data-action can't reach it. We
// hand off with a dispatched window event instead -- field:photo-viewer:open,
// carrying this question's own URLs and the tapped index -- the same handoff
// shape field:photo-camera:open already uses for the Take Photo button. Works
// offline and for local blob: URLs since no server round-trip is involved.
//
// Each thumb also carries a delete badge (card #4744) -- a visible, 44px-target
// X in the top-right corner. Tapping it confirms once (window.confirm, no
// custom modal needed for a single destructive action) then calls `onDelete`
// with the photo's url; the caller (result_editor_controller.js) owns actually
// removing it from the question via lib/photo_removal.js's single removal
// function, so this module stays a pure render layer.
export function renderPhotoPreview(el, value, { onDelete } = {}) {
  let strip = el.querySelector('[data-photo-camera-target="preview"]')
  if (!strip) {
    strip = document.createElement("div")
    strip.setAttribute("data-photo-camera-target", "preview")
    strip.className = "flex gap-2 overflow-x-auto mt-3 pb-1"
    strip.addEventListener("click", (event) => {
      const deleteBtn = event.target.closest("[data-photo-delete-url]")
      if (deleteBtn) {
        event.stopPropagation()
        const url = deleteBtn.dataset.photoDeleteUrl
        if (strip._onDelete && window.confirm("Delete this photo?")) strip._onDelete(url)
        return
      }
      const img = event.target.closest("img[data-photo-viewer-index]")
      if (!img) return
      window.dispatchEvent(new CustomEvent("field:photo-viewer:open", {
        detail: { urls: strip._viewerUrls || [], index: Number(img.dataset.photoViewerIndex), onDelete: strip._onDelete }
      }))
    })
    el.appendChild(strip)
  }

  const urls = previewUrls(value)
  strip._viewerUrls = urls
  strip._onDelete = onDelete
  if (!urls.length) {
    strip.innerHTML = ""
    strip.classList.add("hidden")
    return strip
  }

  strip.classList.remove("hidden")
  strip.innerHTML = urls
    .map(
      (url, index) => `
      <div class="relative shrink-0">
        <img src="${url}"
             data-photo-viewer-index="${index}"
             class="h-20 w-20 rounded-lg object-cover border border-slate-200 shrink-0 cursor-pointer"
             alt="Captured photo">
        ${onDelete ? DELETE_BUTTON_HTML.replace("__URL__", url) : ""}
      </div>
    `
    )
    .join("")
  return strip
}
